import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Play, ArrowRight, CheckCircle, FileText, DollarSign, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import newLogo from '../assets/images/new-logo.png';

const HowItWorks = () => {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleStartNow = () => {
    navigate('/start');
  };

  const steps = [
    {
      step: 1,
      title: "Upload Your Script",
      description: "Simply upload your PDF script and provide project context like budget, genre, and target audience.",
      icon: FileText,
      details: "Our system accepts PDF files up to 50MB and extracts text with precise page and line mapping."
    },
    {
      step: 2,
      title: "AI Analysis",
      description: "Our advanced AI scans your script to identify commercial integration opportunities.",
      icon: TrendingUp,
      details: "We detect logo placements, product usage, and mention opportunities with detailed reasoning and brand suggestions."
    },
    {
      step: 3,
      title: "Review & Select",
      description: "Review AI suggestions with confidence scores and select the integrations that fit your vision.",
      icon: CheckCircle,
      details: "Each suggestion includes example brands, alternatives, and value ranges to help you make informed decisions."
    },
    {
      step: 4,
      title: "Budget Impact",
      description: "See real-time calculations of how sponsorships could bridge your production budget gap.",
      icon: DollarSign,
      details: "Get baseline AdSense estimates plus sponsorship potential with confidence metrics tied to your reach goals."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-25 to-orange-50">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header with Logo */}
        <div className="flex items-center justify-between mb-12">
          <Button variant="ghost" onClick={handleBackToHome} className="hover:bg-yellow-50">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <img
            src={newLogo}
            alt="Artie Logo"
            className="h-12 opacity-90"
          />
        </div>

        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-tight">
            How It Works
          </h1>
          <p className="text-xl text-gray-600 font-light">
            See how Artie transforms your scripts into predictable revenue streams
          </p>
        </div>

        {/* Video Section */}
        <Card className="mb-20 border-0 shadow-sm bg-white/70 backdrop-blur-sm">
          <CardContent className="p-12">
            <div className="bg-gray-900 rounded-2xl aspect-video flex items-center justify-center mb-8">
              <div className="text-center text-white">
                <Play className="h-20 w-20 mx-auto mb-6 opacity-80 cursor-pointer hover:opacity-100 transition-opacity" />
                <h3 className="text-2xl font-light mb-3">Product Demo Video</h3>
                <p className="text-gray-300 text-lg font-light">
                  Watch how creators go from script analysis to guaranteed revenue
                </p>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-light text-gray-900 mb-4">
                See the Complete Workflow in Action
              </h3>
              <p className="text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
                This 3-minute demo shows you exactly how our AI identifies commercial opportunities 
                in real scripts and calculates the budget impact for content creators like you.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Process Steps */}
        <div className="mb-20">
          <h2 className="text-3xl font-light text-gray-900 text-center mb-16">
            Four Simple Steps to Predictable Revenue
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <Card key={step.step} className="relative border-0 shadow-sm hover:shadow-md transition-shadow duration-300 bg-white/70 backdrop-blur-sm">
                  <CardHeader className="pb-6">
                    <div className="flex items-center space-x-6">
                      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                        <Icon className="h-8 w-8 text-yellow-600" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-light">
                          Step {step.step}: {step.title}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-gray-600 mb-4 font-light leading-relaxed">{step.description}</p>
                    <p className="text-sm text-gray-500 font-light">{step.details}</p>
                  </CardContent>
                  {index % 2 === 0 && index < steps.length - 1 && (
                    <div className="hidden md:block absolute -right-6 top-1/2 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-yellow-400" />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Benefits Section */}
        <Card className="mb-20 bg-yellow-50/80 border-yellow-200 backdrop-blur-sm">
          <CardContent className="p-16">
            <h3 className="text-3xl font-light text-gray-900 text-center mb-16">
              Why Content Creators Choose Artie
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="text-4xl font-light text-yellow-600 mb-4">85%</div>
                <div className="text-gray-800 font-medium mb-3">Revenue Increase</div>
                <div className="text-gray-700 text-sm font-light">
                  Average increase in sponsorship revenue compared to traditional methods
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-light text-yellow-600 mb-4">40hrs</div>
                <div className="text-gray-800 font-medium mb-3">Time Saved Monthly</div>
                <div className="text-gray-700 text-sm font-light">
                  Hours saved per month on sponsor outreach and negotiations
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-light text-yellow-600 mb-4">92%</div>
                <div className="text-gray-800 font-medium mb-3">Accuracy Rate</div>
                <div className="text-gray-700 text-sm font-light">
                  AI suggestions rated as relevant and actionable by creators
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="bg-gradient-to-r from-yellow-400 to-amber-400 text-gray-900 border-0 shadow-lg">
          <CardContent className="p-16 text-center">
            <h3 className="text-3xl font-light mb-6">
              Ready to Transform Your Content Revenue?
            </h3>
            <p className="text-gray-800 mb-12 text-xl max-w-3xl mx-auto font-light leading-relaxed">
              Join thousands of creators who have already discovered hidden revenue opportunities 
              in their scripts. Start your first analysis today.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                onClick={handleStartNow}
                size="lg"
                variant="secondary"
                className="flex items-center gap-3 px-12 py-6 text-xl bg-white hover:bg-gray-50 text-gray-900 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Start Your First Analysis
                <ArrowRight className="h-6 w-6" />
              </Button>
            </div>
            <p className="text-gray-700 text-sm mt-6 font-light">
              No credit card required • Free demo script available
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HowItWorks;